import { Injectable, Logger, NotFoundException } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { financialYearOf } from './financial-year';
import { InvoiceKind, InvoiceNumberService } from './invoice-number.service';
import {
  PartySnapshot, SellerSnapshot, emptyParty, partyFromAddress,
} from './invoice-snapshot';

/**
 * Issues invoices, once each.
 *
 * "Issue" here means allocating the number and freezing the snapshots — the PDF
 * is a separate, later, retryable step (`InvoicePdfService`). Splitting them
 * matters: the legal record must not depend on a headless browser starting.
 * If Chromium is broken on the box, the invoice still exists with its number
 * and its snapshots, and the paper catches up when it is fixed.
 */
@Injectable()
export class InvoiceService {
  private readonly logger = new Logger(InvoiceService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly numbers: InvoiceNumberService
  ) {}

  /**
   * The invoice for an order, issuing it if this is the first time anyone has
   * asked.
   *
   * Concurrency is handled by the unique index on `orderId` rather than a lock:
   * two first-time requests both try to insert, one wins, and the loser reads
   * the winner's row instead of raising a second document with a second number.
   */
  async ensureInvoice(orderId: string, actor = 'system') {
    const existing = await this.prisma.invoice.findFirst({
      where: { orderId, kind: 'tax_invoice' },
    });
    if (existing) return existing;

    try {
      return await this.issue(orderId, 'tax_invoice', actor);
    } catch (error) {
      // P2002: the other request got there first. Its row is the invoice.
      if ((error as { code?: string })?.code === 'P2002') {
        const winner = await this.prisma.invoice.findFirst({
          where: { orderId, kind: 'tax_invoice' },
        });
        if (winner) return winner;
      }
      throw error;
    }
  }

  private async issue(orderId: string, kind: InvoiceKind, actor: string) {
    const order = await this.prisma.order.findUnique({ where: { id: orderId } });
    if (!order) throw new NotFoundException('Order not found');

    const [store, branding] = await Promise.all([
      this.prisma.store.findUnique({ where: { id: order.storeId } }),
      this.prisma.storeBranding.findFirst({ where: { storeId: order.storeId } }),
    ]);
    if (!store) throw new NotFoundException('Store not found');

    const organization = store.organizationId
      ? await this.prisma.organization.findUnique({ where: { id: store.organizationId } })
      : null;

    const customer = order.customerId
      ? await this.prisma.customer.findUnique({ where: { id: order.customerId } })
      : null;

    const seller = this.sellerSnapshot(store, branding, organization);
    const buyer = this.buyerSnapshot(order, customer);

    // Whether this was an intra- or inter-state supply was decided at checkout
    // and is already recorded per line. Reading it from the lines rather than
    // re-deriving it from today's store state keeps the invoice consistent with
    // the tax that was actually charged.
    const lines = await this.prisma.orderItem.findMany({
      where: { orderId },
      select: { isInterState: true, taxableValue: true },
    });

    const isInterState = lines.some((line) => line.isInterState) || order.igstTotal > 0;
    const taxableAmount = lines.reduce((sum, line) => sum + line.taxableValue, 0);

    const issuedAt = new Date();
    const financialYear = financialYearOf(issuedAt);

    return this.prisma.$transaction(async (tx) => {
      // Allocated last: everything that can fail has now succeeded, so the
      // number is not burned on a failure and the series stays gapless.
      const { serial, invoiceNumber } = await this.numbers.allocate(
        tx,
        order.storeId,
        financialYear,
        kind
      );

      const invoice = await tx.invoice.create({
        data: {
          storeId: order.storeId,
          organizationId: store.organizationId ?? null,
          orderId: order.id,
          kind,
          invoiceNumber,
          financialYear,
          serial,
          issuedAt,

          sellerSnapshot: seller as unknown as object,
          buyerSnapshot: buyer as unknown as object,
          billingAddress: (order.billingAddress ?? undefined) as object | undefined,
          shippingAddress: (order.shippingAddress ?? undefined) as object | undefined,
          placeOfSupply: buyer.state ?? seller.state ?? null,
          isInterState,

          currency: order.currency,
          subtotal: order.subtotal,
          discountTotal: order.discountTotal,
          taxableAmount: taxableAmount || order.subtotal - order.discountTotal,
          cgstTotal: order.cgstTotal,
          sgstTotal: order.sgstTotal,
          igstTotal: order.igstTotal,
          tcsTotal: order.tcsTotal,
          shippingTotal: order.shippingTotal,
          grandTotal: order.grandTotal,

          createdBy: actor,
          updatedBy: actor,
        },
      });

      this.logger.log(`issued ${invoiceNumber} for order ${order.orderNumber}`);
      return invoice;
    });
  }

  /**
   * The seller as they are today, frozen.
   *
   * The address comes from StoreBranding — the same fields the Local & map tab
   * edits — because that is the address the merchant has actually curated for
   * customers. Store.city/state are the fallback, and the organisation supplies
   * whatever is still missing.
   */
  private sellerSnapshot(
    store: { name: string; gstin: string | null; city: string | null; state: string | null; country: string | null; location: string | null },
    branding: {
      streetAddress: string | null; locality: string | null; region: string | null;
      postalCode: string | null; countryCode: string; contactPhone: string | null;
    } | null,
    organization: { name: string; gstin: string | null; email: string | null; phone: string | null; city: string | null; state: string | null } | null
  ): SellerSnapshot {
    const addressLines = [branding?.streetAddress ?? store.location]
      .map((line) => line?.trim())
      .filter((line): line is string => !!line);

    return {
      name: store.name,
      addressLines,
      city: branding?.locality ?? store.city ?? organization?.city ?? null,
      state: branding?.region ?? store.state ?? organization?.state ?? null,
      postalCode: branding?.postalCode ?? null,
      country: branding?.countryCode ?? store.country ?? 'IN',
      // The store's own registration first: a multi-store merchant may hold one
      // per state, and the invoice must carry the one that made this supply.
      gstin: store.gstin ?? organization?.gstin ?? null,
      email: organization?.email ?? null,
      phone: branding?.contactPhone ?? organization?.phone ?? null,
      logoUrl: null,
      // Bank details are not modelled yet; the block is omitted rather than
      // printed empty. See docs/20-invoicing-plan.md.
      bank: null,
    };
  }

  private buyerSnapshot(
    order: { billingAddress: unknown; shippingAddress: unknown; email: string | null },
    customer: { firstName: string | null; lastName: string | null; email: string; phone: string | null } | null
  ): PartySnapshot {
    const fallbackName =
      [customer?.firstName, customer?.lastName].filter(Boolean).join(' ').trim() || 'Customer';

    const address = order.billingAddress ?? order.shippingAddress;
    if (!address) {
      const party = emptyParty(fallbackName);
      party.email = order.email ?? customer?.email ?? null;
      party.phone = customer?.phone ?? null;
      return party;
    }

    return partyFromAddress(address, fallbackName, order.email ?? customer?.email ?? null);
  }
}
